import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Shield, 
  Globe, 
  Zap, 
  Code, 
  Play, 
  Server,
  Check
} from 'lucide-react';

export const CDNPage = () => {
  const features = [
    {
      icon: Shield,
      title: 'Dual WAAP',
      description: 'Test security rules in Audit Mode against production traffic before enforcing them to mitigate false positives.'
    },
    {
      icon: Globe,
      title: 'Holistic Protection',
      description: 'Layer 3/4/7 DDoS mitigation, Bot Management, and Client-Side Protection delivered at the edge for fast, coordinated protection.'
    },
    {
      icon: Zap,
      title: 'Anycast DNS',
      description: 'Route users to the absolute closest PoP with our high-performance, fault-tolerant DNS infrastructure.'
    },
    {
      icon: Code,
      title: 'Rules Engine',
      description: 'Granular control over request handling. Override headers, caching policies, and routes at the edge.'
    },
    {
      icon: Play,
      title: 'Video Streaming',
      description: 'Native HLS & MPEG-DASH support, live streaming ingest (RTMP), and adaptive bitrate delivery.'
    },
    {
      icon: Server,
      title: 'Origin Shield',
      description: 'Collapse requests to reduce load on your origin servers and increase cache hit ratios.'
    }
  ];

  const streamingFeatures = [
    { title: 'Live Event Scaling', description: 'Proven capacity for the world\'s largest sporting events.' },
    { title: 'Small Object Delivery', description: 'Optimized for HLS/DASH segments.' },
    { title: 'Token Auth', description: 'Secure your premium content with advanced access controls.' }
  ];

  const rulesEngineFeatures = [
    { title: 'Header Manipulation', description: 'Rewrite, add, or delete headers at the edge.' },
    { title: 'Cache Keys', description: 'Define custom cache keys based on query params or cookies.' },
    { title: 'Origin Selection', description: 'Dynamically route to different origins based on path or user agent.' }
  ];

  return (
    <div className="bg-[var(--bg-primary)] min-h-screen">
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-8 lg:px-12 text-center">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[#3B82F6]/10 border border-[#3B82F6]/30 px-4 py-2 mb-8 text-sm text-[#3B82F6] rounded-full">
            <Globe size={16} />
            Parler CDN
            <span className="bg-[#3B82F6] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ml-1">
              Coming Soon
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            The legendary <span className="text-[#3B82F6]">Edgecast</span> network,
            <br />now at your command.
          </h1>

          <p className="text-base md:text-lg lg:text-xl text-white/70 max-w-3xl mx-auto mb-10">
            Deliver content globally with the infrastructure that powered the internet's biggest streams. 
            180+ Super PoPs, 250+ Tbps capacity, and enterprise-grade security.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/packages?tab=cdn" 
              className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] text-white px-8 py-3.5 font-semibold hover:bg-[#2563EB] transition-all rounded-xl hover:-translate-y-1"
            >
              Get Started
            </Link>
            <Link 
              to="/packages?tab=cdn" 
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/20 text-white px-8 py-3.5 font-semibold hover:bg-white/5 transition-all rounded-xl hover:-translate-y-1"
            >
              See CDN Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-12 md:mb-16">
            Built for performance at scale
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={idx} 
                  className="bg-[var(--bg-secondary)] border border-white/10 p-6 md:p-8 hover:border-[#3B82F6]/50 transition-all rounded-2xl hover:-translate-y-1"
                >
                  <Icon size={28} className="text-[#3B82F6] mb-4" />
                  <h3 className="text-lg md:text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm md:text-base text-white/60 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Media & Entertainment Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-[var(--bg-secondary)]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#3B82F6]/10 border border-[#3B82F6]/30 px-3 py-1.5 mb-6 text-xs text-[#3B82F6] rounded-full">
                <Play size={14} />
                Media & Entertainment
              </div>

              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
                Streaming perfection.
              </h2>

              <p className="text-base md:text-lg text-white/70 mb-8">
                Parler CDN (Edgecast) set the standard for internet video. Deliver buffer-free 
                experiences for VOD and Live events.
              </p>

              <div className="space-y-4">
                {streamingFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Check size={20} className="text-[#3B82F6] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-white font-medium">{feature.title}:</span>
                      <span className="text-white/60 ml-1">{feature.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Video Player Mockup */}
            <div className="bg-[#1a1f2e] border border-white/10 rounded-2xl overflow-hidden">
              <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-[#1a1f2e] to-[#0d1526]">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-white/10 rounded-full flex items-center justify-center">
                  <Play size={32} className="text-white ml-1" />
                </div>
              </div>
              <div className="p-4 border-t border-white/10">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  <span className="text-red-500 text-xs font-medium">LIVE</span>
                </div>
                <div className="text-white font-semibold">Global Summit Keynote</div>
                <div className="text-white/50 text-sm">4K HDR • 60 FPS • 12ms Latency</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rules Engine Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Code Block Mockup */}
            <div className="order-2 lg:order-1">
              <div className="bg-[#1a1f2e] border border-white/10 rounded-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
                  <span className="w-3 h-3 rounded-full bg-red-500"></span>
                  <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  <span className="text-white/50 text-sm ml-2">rules.json</span>
                </div>
                <pre className="p-4 text-sm overflow-x-auto">
                  <code className="text-white/80">
{`if (request.path.match("/api/*")) {
  `}<span className="text-[#3B82F6]">edge.cache.bypass</span>{`();
  `}<span className="text-[#3B82F6]">origin.set</span>{`("api-backend");
}

else if (request.geo.country == "FR") {
  `}<span className="text-[#3B82F6]">response.header.set</span>{`("X-Hello", "Bonjour");
}`}
                  </code>
                </pre>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-[#3B82F6]/10 border border-[#3B82F6]/30 px-3 py-1.5 mb-6 text-xs text-[#3B82F6] rounded-full">
                <Code size={14} />
                Programmable Edge
              </div>

              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
                Total control with Rules Engine.
              </h2>

              <p className="text-base md:text-lg text-white/70 mb-8">
                Don't settle for "black box" caching. Our Rules Engine gives you granular control over 
                every request, header, and cookie.
              </p>

              <div className="space-y-4">
                {rulesEngineFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Check size={20} className="text-[#3B82F6] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-white font-medium">{feature.title}:</span>
                      <span className="text-white/60 ml-1">{feature.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-[var(--bg-secondary)]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
            Pair with Parler Cloud
          </h2>
          <p className="text-base md:text-lg text-white/70 mb-8">
            Get lower origin load and simpler networking when you host your backend on Parler Cloud.
          </p>
          <Link 
            to="/cloud" 
            className="inline-flex items-center gap-2 bg-transparent border border-[#3B82F6] text-[#3B82F6] px-8 py-3.5 font-semibold hover:bg-[#3B82F6]/10 transition-all rounded-xl hover:-translate-y-1"
          >
            Explore Parler Cloud
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CDNPage;
